import { useCallback, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { cn } from './cn';

export interface PaginationProps {
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** Show "총 1,234건 중 21-40" Korean style summary. Default true. */
  showTotalSummary?: boolean;
  /** Render a go-to-page input on the right. Default true. */
  showGoTo?: boolean;
}

const BASE_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-button)',
  border: '1px solid var(--divider-strong)',
  background: 'var(--surface-0)',
  color: 'var(--content-default)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  transition: 'background-color 120ms, border-color 120ms, color 120ms',
};

const ACTIVE_BTN: CSSProperties = {
  ...BASE_BTN,
  background: 'var(--accent-default)',
  color: 'var(--cta-primary-on)',
  borderColor: 'var(--accent-default)',
};

const DISABLED_BTN: CSSProperties = {
  ...BASE_BTN,
  opacity: 0.4,
  cursor: 'not-allowed',
};

const formatKoNumber = (n: number): string => n.toLocaleString('ko-KR');

export const Pagination = ({
  total,
  currentPage,
  pageSize,
  onPageChange,
  className,
  showTotalSummary = true,
  showGoTo = true,
}: PaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, total);
  const end = Math.min(currentPage * pageSize, total);
  const [goToValue, setGoToValue] = useState<string>('');

  const pages = useMemo(() => {
    const WINDOW = 2;
    const out: number[] = [];
    const from = Math.max(1, currentPage - WINDOW);
    const to = Math.min(totalPages, currentPage + WINDOW);
    for (let i = from; i <= to; i += 1) out.push(i);
    return out;
  }, [currentPage, totalPages]);

  const firstPage = pages.length > 0 ? pages[0] : undefined;
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;

  const handleGoTo = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const parsed = Number.parseInt(goToValue, 10);
      if (Number.isNaN(parsed)) return;
      const clamped = Math.min(Math.max(1, parsed), totalPages);
      onPageChange(clamped);
      setGoToValue('');
    },
    [goToValue, onPageChange, totalPages],
  );

  return (
    <nav
      className={cn('flex flex-wrap items-center justify-between gap-3', className)}
      aria-label="페이지 탐색"
    >
      {showTotalSummary ? (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          총 {formatKoNumber(total)}건 중 {formatKoNumber(start)}-{formatKoNumber(end)}
        </span>
      ) : (
        <span aria-hidden="true" />
      )}

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            style={currentPage <= 1 ? DISABLED_BTN : BASE_BTN}
            onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="이전 페이지"
          >
            &#8249;
          </button>

          {firstPage !== undefined && firstPage > 1 ? (
            <>
              <button type="button" style={BASE_BTN} onClick={() => onPageChange(1)}>
                1
              </button>
              {firstPage > 2 ? (
                <span aria-hidden="true" style={{ color: 'var(--content-muted)', fontSize: 12 }}>
                  &#8230;
                </span>
              ) : null}
            </>
          ) : null}

          {pages.map((p) => (
            <button
              key={p}
              type="button"
              style={p === currentPage ? ACTIVE_BTN : BASE_BTN}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? 'page' : undefined}
              aria-label={`${p} 페이지`}
            >
              {p}
            </button>
          ))}

          {lastPage !== undefined && lastPage < totalPages ? (
            <>
              {lastPage < totalPages - 1 ? (
                <span aria-hidden="true" style={{ color: 'var(--content-muted)', fontSize: 12 }}>
                  &#8230;
                </span>
              ) : null}
              <button type="button" style={BASE_BTN} onClick={() => onPageChange(totalPages)}>
                {totalPages}
              </button>
            </>
          ) : null}

          <button
            type="button"
            style={currentPage >= totalPages ? DISABLED_BTN : BASE_BTN}
            onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="다음 페이지"
          >
            &#8250;
          </button>
        </div>

        {showGoTo && totalPages > 1 ? (
          <form onSubmit={handleGoTo} className="flex items-center gap-1.5">
            <label
              htmlFor="pagination-goto"
              className="text-[12px]"
              style={{ color: 'var(--content-muted)' }}
            >
              이동
            </label>
            <input
              id="pagination-goto"
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              value={goToValue}
              onChange={(e) => setGoToValue(e.target.value)}
              className="h-8 w-14 rounded-md text-center text-[12px] outline-none focus:[box-shadow:var(--focus-ring-brand)]"
              style={{
                background: 'var(--surface-0)',
                border: '1px solid var(--divider-strong)',
                color: 'var(--content-default)',
              }}
              aria-label="이동할 페이지 번호"
            />
            <button
              type="submit"
              style={BASE_BTN}
              aria-label="페이지 이동"
            >
              이동
            </button>
          </form>
        ) : null}
      </div>
    </nav>
  );
};
